const API_BASE = '/api'

export interface FormField {
  id: string
  name: string
  type: 'text' | 'number' | 'email' | 'tel'
  required: boolean
}

export interface RecipientLink {
  id: string
  token: string
  label: string
  defaults: Record<string, string>
}

export interface FormPublicData {
  id: string
  title: string
  fileType: 'pdf' | 'image'
  fields: FormField[]
  defaults: Record<string, string>
  linkLabel: string
}

export interface FormPrivateData extends FormPublicData {
  createdAt: string
  recipientLinks: RecipientLink[]
  signatureCount: number
  isActivated: boolean
  ownerUserId: string | null
}

export interface SignatureEntry {
  id: string
  formId: string
  linkId: string | null
  signedAt: string
  fields: Record<string, string>
  signatureImageUrl: string
}

// Helper to safely parse JSON response
async function parseJsonResponse(response: Response): Promise<any> {
  const text = await response.text()
  if (!text) {
    throw new Error('Empty response from server')
  }
  try {
    return JSON.parse(text)
  } catch {
    throw new Error(`Invalid response: ${text.substring(0, 100)}`)
  }
}

// Get the stored form key (owner only — for transparent encryption)
export async function getFormKey(formId: string): Promise<{ password: string }> {
  const response = await fetch(`${API_BASE}/forms/${formId}/key`, {
    credentials: 'include',
  })
  if (!response.ok) {
    const error = await parseJsonResponse(response)
    throw new Error(error.error || 'Failed to get form key')
  }
  return response.json()
}

// Create a new form
export async function createForm(
  file: File,
  title: string,
  password: string | null,
  fields: FormField[],
  termsAccepted?: boolean
): Promise<{ id: string }> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('title', title)
  if (password) formData.append('password', password)
  formData.append('fields', JSON.stringify(fields))
  if (termsAccepted) formData.append('termsAccepted', 'true')

  const response = await fetch(`${API_BASE}/forms`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  const data = await parseJsonResponse(response)

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create form')
  }

  return data
}

// Delete a form (owner only)
export async function deleteForm(formId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/forms/${formId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!response.ok) {
    const error = await parseJsonResponse(response)
    throw new Error(error.error || 'Failed to delete form')
  }
}

// Get form public data (for signers)
export async function getFormPublic(formId: string, token?: string): Promise<FormPublicData> {
  const url = token
    ? `${API_BASE}/forms/${formId}?token=${token}`
    : `${API_BASE}/forms/${formId}`

  const response = await fetch(url)

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get form')
  }

  return response.json()
}

// Get form file URL (direct endpoint)
export function getFormFileUrl(formId: string): string {
  return `${API_BASE}/forms/${formId}/file`
}

// Verify password and get full form data
export async function verifyAndGetForm(formId: string, password?: string): Promise<FormPrivateData> {
  const response = await fetch(`${API_BASE}/forms/${formId}/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: password || undefined }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to verify password')
  }

  return response.json()
}

// Create recipient-specific link
export async function createRecipientLink(
  formId: string,
  password: string | undefined,
  label: string,
  defaults: Record<string, string>
): Promise<RecipientLink> {
  const response = await fetch(`${API_BASE}/forms/${formId}/links`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: password || undefined, label, defaults }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create link')
  }

  return response.json()
}

// Delete recipient link
export async function deleteRecipientLink(
  formId: string,
  linkId: string,
  password?: string
): Promise<void> {
  const response = await fetch(`${API_BASE}/forms/${formId}/links/${linkId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: password || undefined }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to delete link')
  }
}

// Submit signature
export async function submitSignature(
  formId: string,
  fields: Record<string, string>,
  signatureImage: string,
  linkToken?: string
): Promise<{ id: string; signedAt: string }> {
  const response = await fetch(`${API_BASE}/signatures`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ formId, fields, signatureImage, linkToken }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to submit signature')
  }

  return response.json()
}

// Get all signatures
export async function getSignatures(
  formId: string,
  password?: string
): Promise<{
  signatures: SignatureEntry[]
  fields: FormField[]
  recipientLinks: RecipientLink[]
  sessionToken: string
}> {
  const response = await fetch(`${API_BASE}/signatures/${formId}/list`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: password || undefined }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get signatures')
  }

  return response.json()
}

// Export as Excel
export async function exportExcel(formId: string, password?: string): Promise<Blob> {
  const response = await fetch(`${API_BASE}/export/${formId}/excel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: password || undefined }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to export Excel')
  }

  return response.blob()
}

// Export as PDF
export async function exportPdf(formId: string, password?: string): Promise<Blob> {
  const response = await fetch(`${API_BASE}/export/${formId}/pdf`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: password || undefined }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to export PDF')
  }

  return response.blob()
}
